/**
 * Transkript-Stream — Bubbles unter dem Orb, Persistenz im LocalStorage.
 *
 * Bubble-Contract (v2):
 *  - Jede Bubble hat eine stabile ID (UUID), durch die Session-Layer sie
 *    gezielt befüllen, finalisieren oder löschen kann.
 *  - User-Bubbles werden bei speech_started als "…"-Placeholder angelegt.
 *    Sie werden entweder per finalize(text) bestätigt, oder per discard()
 *    wieder weggeräumt (Whisper hat nichts geliefert). So sieht Selman
 *    JEDE Sprach-Anhebung sofort, ohne Reihenfolge-Drift.
 *  - Assistant-Bubbles streamen via setText(); pro Stream eine Bubble.
 *  - Tool-Badges sind keine eigenen Bubbles, sondern werden inline an die
 *    aktuelle User-Bubble (oder als Sibling, wenn keine offene User-Bubble)
 *    angehängt — damit ist die Reihenfolge "Du redest → Tool läuft →
 *    JARVIS antwortet" visuell konsistent.
 *
 * Auto-Scroll an's Ende solange Nutzer bereits unten ist. Wenn der Nutzer
 * manuell nach oben gescrollt hat, wird Auto-Scroll pausiert (kein Hijack).
 */

const STORAGE_KEY = "jarvis.transcript.v1";
const MAX_TURNS = 50;

export type TranscriptRole = "you" | "jarvis" | "system" | "error";

export interface TranscriptTurn {
  role: TranscriptRole;
  text: string;
  ts: number;
  imageUrl?: string;
}

/**
 * Handle auf eine konkrete Bubble — gibt der Session-Schicht volle Kontrolle
 * über Lifecycle. Alle Methoden sind no-op nach `discard()`/`finalize()`.
 */
export interface BubbleHandle {
  readonly id: string;
  readonly role: TranscriptRole;
  /** Direkter Lese-Zugriff auf das Body-Element für Edge-Cases. */
  readonly bodyEl: HTMLElement;
  /** Direkter Lese-Zugriff aufs Bubble-Root (für CSS-Class-Tricks). */
  readonly bubble: HTMLElement;
  /** Streaming-Append (für SSE-Chat). Aggregierter Text wird gespeichert. */
  append(piece: string): void;
  /** Hartes Set (für Realtime-Aggregate-Updates wo der Server schon den Vollblick liefert). */
  setText(text: string): void;
  /** Bild an die Bubble hängen (DALL-E-Renderer). */
  setImage(url: string): void;
  /** Bubble final festschreiben — gelangt in LocalStorage. */
  finalize(text?: string): void;
  /** Bubble wieder wegräumen (Whisper-Fail / Phantom). */
  discard(): void;
  /** Inline-Tool-Badge anhängen (rechts neben/unter Body). */
  attachToolBadge(name: string): ToolBadgeHandle;
}

export interface ToolBadgeHandle {
  setStatus(ok: boolean): void;
  remove(): void;
}

export class Transcript {
  readonly root: HTMLElement;
  private listEl: HTMLElement;
  private autoScroll = true;
  private turns: TranscriptTurn[] = [];
  /** Live-Map aller momentan offenen (nicht-finalized) Bubbles. */
  private liveBubbles = new Map<string, HTMLElement>();
  /**
   * Die jeweils zuletzt geöffnete User-Bubble — Tool-Badges hängen sich dort
   * an, damit visuell klar ist "auf Selmans Frage hin wurde Tool X gerufen".
   */
  private currentUserBubbleId: string | null = null;

  constructor(parent: HTMLElement) {
    const wrap = document.createElement("div");
    wrap.className = "transcript";
    wrap.innerHTML = `
      <div class="transcript-list" role="log" aria-live="polite"></div>
    `;
    parent.appendChild(wrap);
    this.root = wrap;
    this.listEl = wrap.querySelector(".transcript-list") as HTMLElement;

    this.listEl.addEventListener("scroll", () => {
      const distFromBottom =
        this.listEl.scrollHeight - this.listEl.scrollTop - this.listEl.clientHeight;
      this.autoScroll = distFromBottom < 32;
    });

    this.loadPersisted();
  }

  /**
   * Fügt einen abgeschlossenen Turn hinzu (sofort persistiert).
   * Für streamende Bubbles `beginBubble()` benutzen.
   */
  appendTurn(role: TranscriptRole, text: string): HTMLElement {
    const turn: TranscriptTurn = { role, text, ts: Date.now() };
    this.turns.push(turn);
    while (this.turns.length > MAX_TURNS) this.turns.shift();
    this.persist();
    return this.renderBubble(turn, this.newId());
  }

  /**
   * Erzeugt eine leere Bubble mit stabiler ID. Lifecycle wird vom Caller
   * gesteuert (append/setText/finalize/discard). Vor finalize ist die Bubble
   * NICHT persistiert — so können wir Placeholder ohne Storage-Lärm zeigen.
   */
  beginBubble(role: TranscriptRole, placeholder?: string): BubbleHandle {
    const id = this.newId();
    const turn: TranscriptTurn = { role, text: placeholder ?? "", ts: Date.now() };
    const el = this.renderBubble(turn, id);
    this.liveBubbles.set(id, el);
    if (role === "you") this.currentUserBubbleId = id;
    const bodyEl = el.querySelector(".bubble-body") as HTMLElement;
    const imageEl = el.querySelector(".bubble-image") as HTMLImageElement | null;
    let aggregated = placeholder && placeholder !== "…" ? placeholder : "";
    let finalized = false;
    let discarded = false;
    const ensureLive = (): boolean => !finalized && !discarded;

    const handle: BubbleHandle = {
      id,
      role,
      get bodyEl() { return bodyEl; },
      get bubble() { return el; },
      append: (piece: string) => {
        if (!ensureLive()) return;
        aggregated += piece;
        bodyEl.textContent = aggregated;
        this.maybeScroll();
      },
      setText: (text: string) => {
        if (!ensureLive()) return;
        aggregated = text;
        bodyEl.textContent = text;
        this.maybeScroll();
      },
      setImage: (url: string) => {
        if (!ensureLive()) return;
        let img = imageEl;
        if (!img) {
          img = document.createElement("img");
          img.className = "bubble-image";
          img.loading = "lazy";
          img.alt = "Generiertes Bild";
          el.appendChild(img);
        }
        img.src = url;
        turn.imageUrl = url;
        this.maybeScroll();
      },
      finalize: (text?: string) => {
        if (!ensureLive()) return;
        finalized = true;
        const finalText = text ?? aggregated;
        bodyEl.textContent = finalText;
        turn.text = finalText;
        // Leere Bubble OHNE Bild → wegräumen statt persistieren (Whisper-Phantom).
        if (!finalText.trim() && !turn.imageUrl) {
          this.fadeOutAndRemove(el);
          this.liveBubbles.delete(id);
          if (this.currentUserBubbleId === id) this.currentUserBubbleId = null;
          return;
        }
        this.turns.push(turn);
        while (this.turns.length > MAX_TURNS) this.turns.shift();
        this.persist();
        this.liveBubbles.delete(id);
        // currentUserBubbleId BEHALTEN — Tool-Badges der laufenden Assistant-
        // Antwort sollen weiterhin an Selmans letzte User-Bubble hängen.
        this.maybeScroll();
      },
      discard: () => {
        if (finalized || discarded) return;
        discarded = true;
        this.fadeOutAndRemove(el);
        this.liveBubbles.delete(id);
        if (this.currentUserBubbleId === id) this.currentUserBubbleId = null;
      },
      attachToolBadge: (name: string) => this.attachToolBadge(el, name),
    };
    return handle;
  }

  /**
   * Tool-Call-Badge — wird primär an `currentUserBubbleId` angehängt (visuell
   * gehört der Tool-Call zu Selmans letzter Frage). Fallback: standalone als
   * Listen-Element.
   */
  appendToolBadge(name: string): ToolBadgeHandle {
    const anchor = this.currentUserBubbleId
      ? this.liveBubbles.get(this.currentUserBubbleId) ??
        this.listEl.querySelector(`[data-bubble-id="${this.currentUserBubbleId}"]`)
      : null;
    if (anchor instanceof HTMLElement) {
      return this.attachToolBadge(anchor, name);
    }
    return this.attachStandaloneToolBadge(name);
  }

  clear(): void {
    this.turns = [];
    this.listEl.innerHTML = "";
    this.liveBubbles.clear();
    this.currentUserBubbleId = null;
    this.persist();
  }

  // ── Internal ────────────────────────────────────────────────────────────

  private attachToolBadge(parent: HTMLElement, name: string): ToolBadgeHandle {
    // Sicherstellen dass der Anchor einen badge-row Container hat
    let row = parent.querySelector(":scope > .bubble-tool-row") as HTMLElement | null;
    if (!row) {
      row = document.createElement("div");
      row.className = "bubble-tool-row";
      parent.appendChild(row);
    }
    return this.renderToolBadge(row, name);
  }

  private attachStandaloneToolBadge(name: string): ToolBadgeHandle {
    return this.renderToolBadge(this.listEl, name);
  }

  private renderToolBadge(container: HTMLElement, name: string): ToolBadgeHandle {
    const icon = toolIcon(name);
    const el = document.createElement("div");
    el.className = "tool-badge is-running";
    el.innerHTML = `
      <span class="tool-icon" aria-hidden="true">${icon}</span>
      <span class="tool-name">${escapeHtml(name)}</span>
      <span class="tool-spinner" aria-hidden="true"></span>
    `;
    container.appendChild(el);
    this.maybeScroll();
    return {
      setStatus: (ok: boolean) => {
        el.classList.remove("is-running");
        el.classList.add(ok ? "is-ok" : "is-fail");
        const spinner = el.querySelector(".tool-spinner") as HTMLElement | null;
        if (spinner) {
          spinner.outerHTML = ok
            ? '<span class="tool-mark tool-mark-ok" aria-hidden="true">✓</span>'
            : '<span class="tool-mark tool-mark-fail" aria-hidden="true">✕</span>';
        }
      },
      remove: () => {
        el.remove();
      },
    };
  }

  private renderBubble(turn: TranscriptTurn, id: string): HTMLElement {
    const el = document.createElement("article");
    el.className = `bubble bubble-${turn.role}`;
    el.dataset.bubbleId = id;
    el.innerHTML = `
      <span class="bubble-role">${turn.role === "you" ? "Du" : turn.role === "jarvis" ? "JARVIS" : turn.role === "error" ? "Fehler" : "System"}</span>
      <span class="bubble-body"></span>
    `;
    const body = el.querySelector(".bubble-body") as HTMLElement;
    body.textContent = turn.text;
    if (turn.imageUrl) {
      const img = document.createElement("img");
      img.className = "bubble-image";
      img.loading = "lazy";
      img.alt = "Generiertes Bild";
      img.src = turn.imageUrl;
      el.appendChild(img);
    }
    this.listEl.appendChild(el);
    this.maybeScroll();
    return el;
  }

  private fadeOutAndRemove(el: HTMLElement): void {
    el.classList.add("is-discarding");
    // Falls die Browser-CSS-Transition nicht greift (reduced-motion),
    // räumen wir trotzdem hart auf nach 240ms.
    setTimeout(() => {
      el.remove();
    }, 240);
  }

  private newId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }

  private maybeScroll(): void {
    if (!this.autoScroll) return;
    requestAnimationFrame(() => {
      this.listEl.scrollTop = this.listEl.scrollHeight;
    });
  }

  private loadPersisted(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      this.turns = parsed
        .filter(
          (t) =>
            t &&
            typeof t.text === "string" &&
            typeof t.role === "string" &&
            ["you", "jarvis", "system", "error"].includes(t.role),
        )
        .slice(-MAX_TURNS);
      for (const turn of this.turns) {
        this.renderBubble({ ...turn, ts: turn.ts || Date.now() }, this.newId());
      }
    } catch {
      // ignore corrupted storage
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.turns));
    } catch {
      // ignore quota errors
    }
  }

  /**
   * Compat-Shim: alte Call-Site `beginStreaming(role)` weiterhin unterstützt,
   * intern via `beginBubble()`. Macht das Refactor minimal-invasiv für die
   * Classic-Whisper-Pipeline (`session.runChatTurn`), die noch das alte
   * append/finalize-Pattern nutzt.
   */
  beginStreaming(role: TranscriptRole): {
    bubble: HTMLElement;
    bodyEl: HTMLElement;
    append(piece: string): void;
    finalize(fullText: string): void;
  } {
    const handle = this.beginBubble(role);
    return {
      bubble: handle.bubble,
      bodyEl: handle.bodyEl,
      append: (piece: string) => handle.append(piece),
      finalize: (fullText: string) => handle.finalize(fullText),
    };
  }
}

/**
 * Ordnet einem Tool-Namen ein passendes Emoji/Icon zu. Default ist ein
 * generisches Tool-Zeichen. Erweitert sich naturgemäß wenn neue Tools dazukommen.
 */
function toolIcon(name: string): string {
  const n = name.toLowerCase();
  if (n.includes("image") || n.includes("dalle") || n.includes("dall_e")) return "🎨";
  if (n.includes("pipeboard") || n.includes("ads") || n.includes("meta_") || n.includes("google_")) return "📊";
  if (n.includes("search") || n.includes("vault") || n.includes("find")) return "🔍";
  if (n.includes("memory") || n.includes("remember") || n.includes("recall")) return "🧠";
  if (n.includes("save") || n.includes("write") || n.includes("store")) return "💾";
  if (n.includes("calendar") || n.includes("schedule") || n.includes("event")) return "📅";
  if (n.includes("mail") || n.includes("email")) return "✉️";
  if (n.includes("sms") || n.includes("text")) return "💬";
  if (n.includes("revenue") || n.includes("payment") || n.includes("money") || n.includes("mrr") || n.includes("mollie")) return "💰";
  if (n.includes("file") || n.includes("doc") || n.includes("read_")) return "📄";
  if (n.includes("web") || n.includes("fetch") || n.includes("url")) return "🌐";
  if (n.includes("claude")) return "🤖";
  return "⚙️";
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      case "'": return "&#39;";
      default: return c;
    }
  });
}
